/**
 * Color transformation algorithms for substrate-specific color adjustments
 * Pure mathematical functions for matrix operations, gamma correction, and CMYK processing
 * Now with memory optimization and WebGL acceleration
 */

import type { CMYK } from '../colorEngine';
import type { ColorTransformMatrix } from '../../types/substrate';
import { performanceColorTransforms } from './performanceColorTransforms';

/**
 * Apply 4x5 transformation matrix to CMYK values with performance optimization
 */
export function applyColorTransform(
  cmyk: CMYK,
  transform: ColorTransformMatrix
): CMYK {
  return performanceColorTransforms.applyColorTransform(cmyk, transform);
}

/**
 * Batch apply color transformations with optimal GPU/CPU routing
 */
export function batchColorTransform(
  colors: CMYK[],
  transform: ColorTransformMatrix
): CMYK[] {
  return performanceColorTransforms.batchTransform(colors, transform);
}

/**
 * Apply gamma correction to CMYK values
 */
export function applyGammaCorrection(cmyk: CMYK, gamma: number): CMYK {
  const applyChannel = (value: number) => {
    const normalized = value / 100;
    const corrected = Math.pow(normalized, 1 / gamma);
    return Math.max(0, Math.min(100, corrected * 100));
  };

  return {
    c: applyChannel(cmyk.c),
    m: applyChannel(cmyk.m),
    y: applyChannel(cmyk.y),
    k: applyChannel(cmyk.k)
  };
}

/**
 * Generate black channel using GCR (Gray Component Replacement)
 */
export function generateBlackGCR(cmyk: CMYK, gcrAmount: number = 0.5): CMYK {
  const gray = Math.min(cmyk.c, cmyk.m, cmyk.y);
  const blackReplacement = gray * gcrAmount;

  return {
    c: Math.max(0, cmyk.c - blackReplacement),
    m: Math.max(0, cmyk.m - blackReplacement),
    y: Math.max(0, cmyk.y - blackReplacement),
    k: Math.min(100, cmyk.k + blackReplacement)
  };
}

/**
 * Apply Under Color Removal (UCR)
 */
export function applyUnderColorRemoval(cmyk: CMYK, ucrAmount: number = 0.3): CMYK {
  const gray = Math.min(cmyk.c, cmyk.m, cmyk.y);
  const removal = gray * ucrAmount;

  return {
    c: Math.max(0, cmyk.c - removal),
    m: Math.max(0, cmyk.m - removal),
    y: Math.max(0, cmyk.y - removal),
    k: Math.min(100, cmyk.k + removal)
  };
}

/**
 * Apply dot gain simulation
 */
export function applyDotGain(cmyk: CMYK, dotGainFactor: number = 0.15): CMYK {
  const applyDotGainChannel = (value: number) => {
    const normalized = value / 100;
    const gain = normalized + (1 - normalized) * dotGainFactor;
    return Math.max(0, Math.min(100, gain * 100));
  };

  return {
    c: applyDotGainChannel(cmyk.c),
    m: applyDotGainChannel(cmyk.m),
    y: applyDotGainChannel(cmyk.y),
    k: applyDotGainChannel(cmyk.k)
  };
}

/**
 * Calculate Total Area Coverage (TAC)
 */
export function calculateTAC(cmyk: CMYK): number {
  return cmyk.c + cmyk.m + cmyk.y + cmyk.k;
}

/**
 * Check if individual ink limits are exceeded
 */
export function checkInkLimits(
  cmyk: CMYK,
  limits: { cyan: number; magenta: number; yellow: number; black: number }
): {
  cyan: { exceeded: boolean; percentage: number; limit: number };
  magenta: { exceeded: boolean; percentage: number; limit: number };
  yellow: { exceeded: boolean; percentage: number; limit: number };
  black: { exceeded: boolean; percentage: number; limit: number };
} {
  return {
    cyan: {
      exceeded: cmyk.c > limits.cyan,
      percentage: cmyk.c,
      limit: limits.cyan
    },
    magenta: {
      exceeded: cmyk.m > limits.magenta,
      percentage: cmyk.m,
      limit: limits.magenta
    },
    yellow: {
      exceeded: cmyk.y > limits.yellow,
      percentage: cmyk.y,
      limit: limits.yellow
    },
    black: {
      exceeded: cmyk.k > limits.black,
      percentage: cmyk.k,
      limit: limits.black
    }
  };
}

/**
 * Optimize CMYK values to stay within ink limits
 */
export function optimizeForInkLimits(
  cmyk: CMYK,
  limits: { cyan: number; magenta: number; yellow: number; black: number },
  tacLimit: number
): CMYK {
  const optimized = { ...cmyk };

  // First, check individual channel limits
  optimized.c = Math.min(optimized.c, limits.cyan);
  optimized.m = Math.min(optimized.m, limits.magenta);
  optimized.y = Math.min(optimized.y, limits.yellow);
  optimized.k = Math.min(optimized.k, limits.black);

  // Then check TAC limit
  const currentTAC = calculateTAC(optimized);
  if (currentTAC > tacLimit) {
    const scale = tacLimit / currentTAC;
    optimized.c *= scale;
    optimized.m *= scale;
    optimized.y *= scale;
    optimized.k *= scale;
  }

  return optimized;
}

/**
 * Calculate ink usage estimation in milliliters
 */
export function calculateInkUsage(
  cmyk: CMYK,
  area: number = 1000, // Area in square centimeters
  inkDensity: number = 0.1 // Ink density in ml/cm² per 100% coverage
): {
  cyan: number;
  magenta: number;
  yellow: number;
  black: number;
  total: number;
} {
  const usageFactor = (area * inkDensity) / 100;

  return {
    cyan: cmyk.c * usageFactor,
    magenta: cmyk.m * usageFactor,
    yellow: cmyk.y * usageFactor,
    black: cmyk.k * usageFactor,
    total: (cmyk.c + cmyk.m + cmyk.y + cmyk.k) * usageFactor
  };
}

/**
 * Create a default color transformation matrix
 */
export function createDefaultTransform(): ColorTransformMatrix {
  return {
    matrix: [
      [1, 0, 0, 0, 0],    // Cyan
      [0, 1, 0, 0, 0],    // Magenta
      [0, 0, 1, 0, 0],    // Yellow
      [0, 0, 0, 1, 0]     // Black
    ],
    type: 'linear',
    inputSpace: 'cmyk',
    outputSpace: 'cmyk',
    gamma: 1.0
  };
}

/**
 * Apply substrate absorption coefficients to simulate ink soaking
 */
export function applyAbsorptionCoefficients(
  cmyk: CMYK,
  absorption: { cyan: number; magenta: number; yellow: number; black: number }
): CMYK {
  return {
    c: cmyk.c * (1 - absorption.cyan),
    m: cmyk.m * (1 - absorption.magenta),
    y: cmyk.y * (1 - absorption.yellow),
    k: cmyk.k * (1 - absorption.black)
  };
}

/**
 * Optimized CMYK transformation that applies all optimizations in sequence
 */
export function getOptimizedCMYK(
  cmyk: CMYK,
  substrate: {
    absorptionCoefficients: { cyan: number; magenta: number; yellow: number; black: number };
    inkLimits: { cyan: number; magenta: number; yellow: number; black: number };
    tacLimit: number;
    colorTransform?: ColorTransformMatrix;
  },
  options: {
    applyAbsorption?: boolean;
    applyColorTransform?: boolean;
    applyGCR?: boolean;
    applyUCR?: boolean;
    applyDotGain?: boolean;
    dotGainFactor?: number;
    gcrAmount?: number;
    ucrAmount?: number;
    customGamma?: number;
  } = {}
): CMYK {
  let optimized = { ...cmyk };

  // Apply absorption coefficients first (simulates ink soaking into substrate)
  if (options.applyAbsorption !== false) {
    optimized = applyAbsorptionCoefficients(optimized, substrate.absorptionCoefficients);
  }

  // Apply color transformation matrix if available
  if (options.applyColorTransform !== false && substrate.colorTransform) {
    optimized = applyColorTransform(optimized, substrate.colorTransform);
  }

  // Apply gamma correction if specified
  const gamma = options.customGamma ?? substrate.colorTransform?.gamma ?? 1.0;
  if (gamma !== 1.0) {
    optimized = applyGammaCorrection(optimized, gamma);
  }

  // Apply black generation and under color removal
  if (options.applyGCR) {
    optimized = generateBlackGCR(optimized, options.gcrAmount ?? 0.5);
  }
  if (options.applyUCR) {
    optimized = applyUnderColorRemoval(optimized, options.ucrAmount ?? 0.3);
  }

  // Apply dot gain simulation
  if (options.applyDotGain !== false) {
    optimized = applyDotGain(optimized, options.dotGainFactor ?? 0.15);
  }

  // Optimize for substrate limits
  optimized = optimizeForInkLimits(optimized, substrate.inkLimits, substrate.tacLimit);

  return optimized;
}

/**
 * Validate transformation matrix dimensions
 */
export function validateTransformMatrix(matrix: number[][]): boolean {
  if (!Array.isArray(matrix) || matrix.length !== 4) {
    return false;
  }

  for (let i = 0; i < 4; i++) {
    if (!Array.isArray(matrix[i]) || matrix[i].length !== 5) {
      return false;
    }
    for (let j = 0; j < 5; j++) {
      if (typeof matrix[i][j] !== 'number' || !isFinite(matrix[i][j])) {
        return false;
      }
    }
  }

  return true;
}
